/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { toast } from '../toast'
import { authApiService } from '../api/auth'
import { useAuthService } from '../../hooks/useAuthService'
import { detectBrowser } from '../../helpers/generalHelper'
import {
  getFileDownloadUrl,
  validateFile as validateFileApi,
  postprocessLocationInfo,
  startCheckingConversion
} from '../api/files'
import { baseApiService, ApiError } from '../api/base'
import { useTranslation } from 'react-i18next'

interface SharedLinkDataType {
  isSharedLinkUrl: string
  sharedLinkUUID: string
  sharedLinkTenantUUID: string
}

export const validateFileFactory = () => {
  const { approverMustViewDocumentPriorToApproval, raUserMustViewDocumentPriorToApproval } = useAuthService()
  const { t } = useTranslation()
  const { requireESignatureForFileCommands } = useAuthService()

  const _getFilePathExtension = (path: string): string => {
    // Handles both Windows and Unix-style paths
    const filename = path.split('\\').pop()?.split('/').pop() || ''
    const lastIndex = filename.lastIndexOf('.')
    if (lastIndex < 1) {
      return ''
    }
    return filename.substring(lastIndex + 1).toLowerCase()
  }

  const getFilePathWithoutExtension = (path: string) => {
    const extension = _getFilePathExtension(path)
    const index = path.lastIndexOf(extension)

    return path.substr(0, index - 1)
  }

  // Download utility function (matches legacy file.service.js)
  const downloadUri = (uri: string, params?: { newTab?: boolean }) => {
    if (uri) {
      const link = document.createElement('a')
      link.href = uri
      link.download = 'document'
      link.target = params?.newTab ? '_blank' : '_self'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const downloadFromSharedLink = async (uuid: string, tenantUuid: string) => {
    try {
      const data = await baseApiService.fetch<{ linkValid: boolean; preSignedUrl?: string }>(
        `/api/v1/sharedlink/${encodeURIComponent(uuid)}/check`
      )
      if (data.linkValid) {
        toast.info(t('BROWSER_DOWNLOAD_IS_PREPARING'))
        if (data.preSignedUrl && data.preSignedUrl.length) {
          downloadUri(data.preSignedUrl)
        } else {
          const downloadUrl = `${baseApiService.getBaseUrl()}/api/v1/sharedlink/${encodeURIComponent(uuid)}/download?tenantUuid=${encodeURIComponent(tenantUuid)}`
          downloadUri(downloadUrl)
        }
      } else {
        toast.error(t('SOMETHING_WENT_WRONG_MSG_ERROR'))
      }
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 404) {
        toast.error(t('MSG_ERROR_LINK_NOT_VALID'))
      } else {
        toast.error(t('SOMETHING_WENT_WRONG_MSG_ERROR'))
      }
    }
  }

  //   const validate = async (fileId: number, successCallback: () => void, failCallback: () => void, file: File) => {
  const validate = async (fileId: number, successCallback: () => void, failCallback: () => void) => {
    // _doValidate(fileId, successCallback, failCallback, null, file)
    // UPDATE AFTER INTEGRATION NAD USE ABOVE LINE
    _doValidate(fileId, successCallback, failCallback)
  }

  const downloadOriginal = async (
    fileId: number,
    downloadOptions?: { newTab: boolean },
    sharedLinkData?: SharedLinkDataType
  ) => {
    if (!sharedLinkData || !sharedLinkData.isSharedLinkUrl) {
      toast.info(t('BROWSER_DOWNLOAD_IS_PREPARING'))
      return new Promise((resolve, reject) => {
        _doValidate(
          fileId,
          async function () {
            try {
              const data = await getFileDownloadUrl(fileId)
              if (data && data.message) {
                toast.info(data.message)
              }
              // Use postprocessLocationInfo to extract download URL
              const fileDownloadInfo = postprocessLocationInfo(data)
              if (fileDownloadInfo.url) {
                downloadUri(fileDownloadInfo.url, downloadOptions)
              }
              resolve(true)
            } catch (error: any) {
              toast.error(error?.body?.message || error?.message || 'Failed to download file')
              reject(error)
            }
          },
          reject
        )
      })
    } else {
      return downloadFromSharedLink(sharedLinkData.sharedLinkUUID, sharedLinkData.sharedLinkTenantUUID)
    }
  }

  const _doValidate = async (fileId?: number, successCallback?: () => void, failCallback?: () => void) => {
    if ((await authApiService.getUserPolicies())?.liveContentVerification && fileId) {
      try {
        const data = await validateFileApi(fileId)

        // File is valid
        if (data.value === true) {
          if (typeof successCallback === 'function') {
            successCallback()
          }
        }
        // File is invalid
        else {
          if (typeof failCallback === 'function') {
            failCallback()
          }
        }
      } catch (error: any) {
        toast.error(t('ERROR_ERROR', { error: error?.body?.message || error?.message || 'Validation failed' }))
        if (typeof failCallback === 'function') {
          failCallback()
        }
      }
    } else {
      if (typeof successCallback === 'function') {
        successCallback()
      } else if (typeof failCallback === 'function') {
        failCallback()
      }
    }
  }

  const getDownloadOriginalFn = (fileId: number) => {
    return () => downloadOriginal(fileId)
  }
  const safeDownloadOriginal = (fileId: number) => {
    const browserType = detectBrowser()
    const downloadOptions = {
      newTab: !!(browserType.safari || browserType.firefox)
    }
    return downloadOriginal(fileId, downloadOptions)
  }

  /**
   * Migrated: downloadZipOfPDFFiles
   * @param selectedIds Array of file IDs to download as PDF zip
   * @param dontAskForEsignature Boolean to skip e-signature modal
   * @param options Context options (staging area, type, etc)
   */
  const downloadZipOfPDFFiles = async (
    selectedIds: number[],
    dontAskForEsignature: boolean = false,
    options: {
      isStagingArea?: boolean
      stagingAreaType?: 'study' | 'site'
      [key: string]: any
    } = {},
    validationFn?: any
  ) => {
    // Helper to select correct API call
    const getApiCall = async (validation?: any) => {
      if (options.isStagingArea && options.stagingAreaType) {
        if (options.stagingAreaType === 'study') {
          // Use new ETMF API for study user docs download
          const { getStudyUserDocsDownload } = await import('../api/etmf')
          return getStudyUserDocsDownload(options, validation)
        } else if (options.stagingAreaType === 'site') {
          const { getSiteUserDocsDownload } = await import('../api/etmf')
          return getSiteUserDocsDownload(options, validation)
        }
      } else {
        // Use new Files API for PDF zip download
        const { getFilesPDFDownloadUrl, postprocessLocationInfo } = await import('../api/files')
        // Return both API response and postprocessed info
        const data = await getFilesPDFDownloadUrl(selectedIds, validation, options)
        return { data, fileDownloadInfo: postprocessLocationInfo(data) }
      }
    }

    // E-signature logic: use signAction from rcm-new/app/hooks/useEsignature.ts
    let validation = null
    if (!dontAskForEsignature) {
      try {
        validation = await validationFn('ADMIN_ACTION_MULTI_DOWNLOAD', 'ADMIN_ACTION_MULTI_DOWNLOAD', {})
      } catch (e) {
        toast.error(t('ESIGNATURE_CANCELLED'))
        return
      }
    }

    try {
      toast.info(t('BROWSER_DOWNLOAD_PASCKAGE_BEING_PROCESSED'))
      const result: any = await getApiCall(validation)
      let data, fileDownloadInfo
      if ('fileDownloadInfo' in result) {
        data = result.data
        fileDownloadInfo = result.fileDownloadInfo
      } else {
        data = result
        fileDownloadInfo = postprocessLocationInfo(data)
      }
      if (data?.message) {
        toast.info(data.message)
      }
      if (fileDownloadInfo?.url) {
        downloadUri(fileDownloadInfo.url, { newTab: false })
      }
    } catch (error: any) {
      toast.error(error?.body?.message || error?.message || t('ERROR_ERROR'))
    }
  }

  /**
   * Migrated: downloadPdf
   * @param fileId File ID to download PDF
   * @param showToast Show toast notification (default: true)
   */
  const downloadPdf = async (fileId: number, showToast: boolean = true) => {
    if (showToast) {
      toast.info(t('BROWSER_DOWNLOAD_IS_PREPARING'))
    }
    await _doValidate(
      fileId,
      async () => {
        try {
          // Dynamically import getFilePdfDownloadUrl and postprocessLocationInfo
          const { getFilePdfDownloadUrl, postprocessLocationInfo } = await import('../api/files')
          const resp = await getFilePdfDownloadUrl(fileId, true)
          if (resp && resp.message) {
            toast.info(resp.message)
          }
          // Use postprocessLocationInfo to extract download URL
          const fileInfo = postprocessLocationInfo(resp)
          if (typeof fileInfo.url === 'string') {
            downloadUri(fileInfo.url)
          } else if (fileInfo.uuidToCheckState) {
            // Start polling for conversion status and auto-download when ready
            startCheckingConversion(fileInfo.uuidToCheckState, fileId)
          } else {
            toast.error(t('GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER'))
          }
        } catch (error: any) {
          if (error?.code === 421) {
            if (showToast) {
              toast.info(t('GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER'))
            }
          } else {
            toast.error(error?.body?.message || error?.message || t('GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER'))
          }
        }
      },
      () => {
        toast.error(t('GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER'))
      }
    )
  }

  /**
   * Migrated: acknowledgeReadingDoc
   * @param fsiId File/FSI ID
   * @param versionId Version ID
   * @param minorVersionId Minor Version ID
   * @param currentViewingTaskId Task ID
   */
  const acknowledgeReadingDoc = async (
    fsiId: number,
    versionId: any,
    minorVersionId: any,
    currentViewingTaskId: any
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (approverMustViewDocumentPriorToApproval() || raUserMustViewDocumentPriorToApproval()) {
        setTimeout(async () => {
          try {
            // Dynamically import getFileViewed and markFileAsViewed
            const { getFileViewed, markFileAsViewed } = await import('../api/files')
            const viewedResp = await getFileViewed(
              fsiId,
              versionId >= 0 ? versionId : null,
              versionId >= 0 ? minorVersionId : null,
              currentViewingTaskId ? (currentViewingTaskId > 0 ? currentViewingTaskId : null) : null
            )
            if (!viewedResp?.viewed) {
              await markFileAsViewed(
                fsiId,
                versionId >= 0 ? versionId : null,
                versionId >= 0 ? minorVersionId : null,
                currentViewingTaskId ?? null
              )
              // Broadcast events using window.dispatchEvent
              window.dispatchEvent(new CustomEvent('doc-marked-as-read', { detail: currentViewingTaskId }))
              window.dispatchEvent(new CustomEvent('originalFileDownloaded'))
              resolve()
            } else {
              resolve()
            }
          } catch (err) {
            reject(err)
          }
        }, 1000)
      } else {
        reject(new Error('User not required to view document'))
      }
    })
  }

  return {
    validate,
    downloadOriginal,
    getDownloadOriginalFn,
    safeDownloadOriginal,
    downloadZipOfPDFFiles,
    _getFilePathExtension,
    getFilePathWithoutExtension,
    downloadPdf,
    acknowledgeReadingDoc
  }
}
